import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Newspaper, Briefcase, TrendingUp, Globe, AlertTriangle, Lock, RefreshCw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { newsAPI } from '../services/api';
import LegalDisclaimer from '../components/LegalDisclaimer';
import CompanyNewsSearchCard from './news/CompanyNewsSearchCard';
import NewsGrid from './news/NewsGrid';

const NEWS_CATEGORIES = [
  { id: 'general', name: 'All News', icon: Globe },
  { id: 'merger', name: 'M&A', icon: Briefcase },
  { id: 'forex', name: 'Forex', icon: TrendingUp },
  { id: 'crypto', name: 'Crypto', icon: Newspaper },
];

const News = () => {
  const { isPro } = useAuth();
  const [activeCategory, setActiveCategory] = useState('general');
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDemo, setIsDemo] = useState(false);
  const [searchSymbol, setSearchSymbol] = useState('');
  const [companyNews, setCompanyNews] = useState([]);
  const [loadingCompany, setLoadingCompany] = useState(false);
  const [sentiment, setSentiment] = useState(null);

  const fetchMarketNews = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await newsAPI.getMarketNews(activeCategory);
      setNews(response.news || []);
      setIsDemo(response.source === 'demo');
    } catch (err) {
      console.error('Error fetching news:', err);
      setError('Failed to load market news');
      setNews([]);
    } finally {
      setLoading(false);
    }
  }, [activeCategory]);

  const fetchCompanyNews = async () => {
    const sym = searchSymbol.trim();
    if (!sym) return;

    setLoadingCompany(true);
    try {
      const [newsResponse, sentimentResponse] = await Promise.all([
        newsAPI.getCompanyNews(sym),
        newsAPI.getSentiment(sym).catch(() => null),
      ]);
      setCompanyNews(newsResponse.news || []);
      setSentiment(sentimentResponse);
    } catch (err) {
      console.error('Error fetching company news:', err);
      setCompanyNews([]);
    } finally {
      setLoadingCompany(false);
    }
  };

  useEffect(() => {
    fetchMarketNews();
  }, [fetchMarketNews]);

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Market News</h1>
            <p className="text-gray-500">Stay informed with real-time market news and analysis</p>
          </div>
          {isDemo && (
            <Badge className="bg-amber-500 text-white">
              <AlertTriangle size={14} className="mr-1" />
              Demo Mode - Configure FINNHUB_API_KEY
            </Badge>
          )}
        </div>

        <CompanyNewsSearchCard
          searchSymbol={searchSymbol}
          setSearchSymbol={setSearchSymbol}
          onSearch={fetchCompanyNews}
          loadingCompany={loadingCompany}
          companyNews={companyNews}
          sentiment={sentiment}
        />

        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {NEWS_CATEGORIES.map((cat) => {
            const Icon = cat.icon;
            return (
              <Button
                key={cat.id}
                variant={activeCategory === cat.id ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveCategory(cat.id)}
                className={activeCategory === cat.id ? 'bg-purple-600' : ''}
                data-testid={`news-cat-${cat.id}`}
              >
                <Icon size={16} className="mr-2" />
                {cat.name}
              </Button>
            );
          })}
          <Button variant="outline" size="sm" onClick={fetchMarketNews} disabled={loading}>
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>

        {error && (
          <Card className="mb-6 border-red-200 bg-red-50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-red-600">
                <AlertTriangle size={20} />
                <span>{error}</span>
                <Button variant="outline" size="sm" onClick={fetchMarketNews} className="ml-auto">
                  Retry
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {loading && (
          <div className="grid lg:grid-cols-2 gap-6">
            {[...Array(4)].map((_, i) => (
              <Card key={`news-skel-${i}`} className="animate-pulse">
                <CardContent className="p-6">
                  <div className="h-4 bg-gray-200 rounded w-1/4 mb-3" />
                  <div className="h-6 bg-gray-200 rounded w-3/4 mb-2" />
                  <div className="h-4 bg-gray-200 rounded w-full mb-4" />
                  <div className="h-4 bg-gray-200 rounded w-1/2" />
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {!loading && news.length > 0 && <NewsGrid news={news} />}

        {!loading && news.length === 0 && !error && (
          <Card className="text-center py-12">
            <CardContent>
              <Newspaper className="mx-auto mb-4 text-gray-400" size={48} />
              <h3 className="font-semibold text-gray-900 mb-2">No News Available</h3>
              <p className="text-gray-500 text-sm">
                {isDemo
                  ? 'Configure FINNHUB_API_KEY in backend/.env to enable real-time news'
                  : 'No news articles found for this category'}
              </p>
            </CardContent>
          </Card>
        )}

        {!isPro && (
          <Card className="mt-8 border-purple-200 bg-purple-50">
            <CardContent className="p-6 text-center">
              <Lock className="mx-auto mb-4 text-purple-600" size={32} />
              <h3 className="font-bold text-lg text-gray-900 mb-2">Upgrade to Pro for Premium Features</h3>
              <p className="text-gray-600 mb-4">
                Get access to real-time alerts, advanced sentiment analysis, and custom news watchlists.
              </p>
              <Button className="bg-purple-600 hover:bg-purple-700">Upgrade to Pro - $19.99/month</Button>
            </CardContent>
          </Card>
        )}

        <LegalDisclaimer variant="compact" />
      </div>
    </div>
  );
};

export default News;
